import { AxiosRequestConfig } from 'axios';
import {
  FulfillableBatchesSpecification,
  FulfillmentOrder,
  GetFulfillmentOrdersResponse,
  JsonApiResponse,
  PaginatedParams,
  PostFulfillmentOrderPayload,
  PostNonAnkorstoreFulfilledOrderPayload,
} from '@monolith/legacy/types/fulfillment';
import http from '@monolith/legacy/services/api/http';
import { deserialize } from '@monolith/legacy/services/utils/jsonapi-parser';
import { get, post, UndeclaredFulfillmentItemsError } from '@monolith/legacy/services/fulfillment/utilities';
import { Fulfillment } from '@ankorstore/api-internal/Fulfillment';
import { Orders } from '@ankorstore/api-internal/Orders';
import { SortEnum6 } from '@ankorstore/api-internal/data-contracts';

export interface GetFulfillmentOrdersParams extends PaginatedParams {
  filter?: {
    /** Request a specific status only */
    status?: boolean;
    /** Request a specific subset of order UUIDs only */
    uuid?: string[];
  };

  sort?: SortEnum6;
}

export const getFulfillmentOrders = async ({
  offset,
  limit,
  filter = {},
  sort = null,
}: GetFulfillmentOrdersParams): Promise<GetFulfillmentOrdersResponse> => {
  const params = {
    'page[limit]': limit,
    'page[offset]': offset,
    ...(filter && { 'filter[uuid]': filter.uuid }),
    ...(sort && { sort }),
  };

  const ordersService = new Orders();
  const response = await ordersService.fulfillmentListOrdersPublic(params);
  return deserialize(response);
};

export const postFulfillmentOrder = async (
  payload: PostFulfillmentOrderPayload | PostNonAnkorstoreFulfilledOrderPayload
): Promise<FulfillmentOrder> => {
  return post('/api/v1/external-orders/non-ankorstore-fulfillment-orders', payload);
};

/** Produces a response that can indicate whether the order can be fulfilled or not. Will not create the order. */
export const validateFulfillmentOrder = async (payload: PostFulfillmentOrderPayload): Promise<FulfillmentOrder> => {
  const hasQuantity = ({ unitQuantity }: FulfillableBatchesSpecification) => unitQuantity > 0;

  const hasFulfillableIdsForItems = payload.items.filter(hasQuantity).every((item) => typeof item.fulfillableId === 'string');

  if (!hasFulfillableIdsForItems) {
    // If no fulfillableIds, then some/all items can be assumed to be undeclared. No need to validate with API.
    throw new UndeclaredFulfillmentItemsError();
  }

  const { items, ...attributes } = payload;

  const jsonApiPayload = {
    data: {
      type: 'fulfillment-orders' as const,
      attributes: {
        ...attributes,
        /** Orders that have been removed completely from the order should not be taken into consideration. */
        items: items.filter(hasQuantity),
      },
    },
  };

  const fulfillmentService = new Fulfillment();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generated type doesn't structurally match FulfillmentOrder
  return (await fulfillmentService.fulfillmentCreateOrder({ validateOrder: true }, jsonApiPayload as any))?.data
    ?.attributes as any;
};

export const getIsFulfillmentOrderFulfillable = async (uuid: string): Promise<null> =>
  get(`/api/v1/orders/${uuid}/is-fulfillable`);

export const getFulfillmentOrder = async (id: string): Promise<JsonApiResponse<FulfillmentOrder>> => {
  const res = await http().get(`/api/v1/fulfillment/orders/${id}?include=shipments`);
  return deserialize(res.data);
};

export const getFulfillmentOrdersByMasterOrderUuid = async (
  masterOrderUuid: string,
  config: AxiosRequestConfig = {}
): Promise<GetFulfillmentOrdersResponse> => {
  const url = `/api/v1/master-orders/${masterOrderUuid}/fulfillment-orders`;

  const response = await http().get<GetFulfillmentOrdersResponse>(url, {
    ...config,
  });

  return deserialize(response.data) as GetFulfillmentOrdersResponse;
};
